import { useEffect, useState, type VideoHTMLAttributes } from 'react';
import type { LocalFile } from './local';
import { localApi, localUrl } from './local';

type Props = Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'> & {
  src: string;
  /** Nombre del archivo si está en esta computadora (se puede arreglar desde la app). */
  localName?: string | null;
  /** Lista nueva de archivos después de arreglarlo. */
  onFixed?: (files: LocalFile[]) => void;
};

/**
 * Un video con aviso cuando la pantalla no lo puede reproducir (H.265, ProRes, 10 bits, audio
 * AC-3…). EasyWorship trae sus propios códecs; las pantallas de KEORA solo abren H.264, VP9 o AV1.
 * Si el archivo está en esta computadora, la app lo convierte con el mismo nombre.
 */
export function LocalVideo({ src, localName = null, onFixed, onError, onLoadedMetadata, ...rest }: Props) {
  const [broken, setBroken] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    setBroken(false);
    setProblem(null);
  }, [src]);
  const local = localName ? localApi() : null;

  async function fix() {
    if (!local?.repair || !localName) return;
    setFixing(true);
    setProblem(null);
    try {
      const r = await local.repair(localName);
      onFixed?.(r.files);
      setBroken(false);
      setVersion((v) => v + 1);
      if (!r.changed) setProblem('El archivo ya estaba en un formato que se reproduce. Si sigue sin verse, puede estar dañado.');
    } catch (e) {
      // Electron antepone «Error invoking remote method…»; se deja solo la causa.
      const why = e instanceof Error ? e.message.replace(/^Error invoking remote method '[^']+': (Error: )?/, '') : '';
      setProblem(`No se pudo convertir este video.${why ? ` Detalle: ${why}` : ''}`);
    } finally {
      setFixing(false);
    }
  }

  if (broken || fixing) {
    return (
      <div className="video-fix" role="status">
        {fixing ? (
          <p>Convirtiendo el video para que se pueda proyectar… Puede tardar unos minutos; no cierres la app.</p>
        ) : (
          <>
            <p>Este video está en un formato que la pantalla no reproduce (EasyWorship sí, porque trae sus propios códecs).</p>
            {local?.repair ? (
              <button type="button" className="btn btn--secondary" onClick={() => void fix()}>
                Convertir para proyectar
              </button>
            ) : localName ? (
              <p className="small">Actualiza KEORA Multimedia (engrane → versión) para poder convertirlo aquí.</p>
            ) : (
              <p className="small">Vuelve a subirlo en MP4 (H.264), o guárdalo en esta computadora desde la app KEORA Multimedia para convertirlo.</p>
            )}
          </>
        )}
        {problem ? <p className="small">{problem}</p> : null}
      </div>
    );
  }
  const url = localName && version > 0 ? `${localUrl(localName)}?v=${version}` : src;
  return (
    <>
      <video
        key={url}
        src={url}
        {...rest}
        onError={(ev) => {
          setBroken(true);
          onError?.(ev);
        }}
        onLoadedMetadata={(ev) => {
          // Sin imagen (videoWidth 0): el navegador abrió el archivo pero no sabe dibujarlo.
          if (ev.currentTarget.videoWidth === 0) setBroken(true);
          onLoadedMetadata?.(ev);
        }}
      />
      {problem ? <p className="small muted">{problem}</p> : null}
    </>
  );
}

/**
 * Revisa todos los videos de esta computadora y convierte los que la pantalla no reproduce (uno
 * por uno, conservando el nombre). Para lo que ya estaba cargado antes de la 1.6.0.
 */
export function useRepairAll(files: readonly LocalFile[] | null, setFiles: (files: LocalFile[]) => void) {
  const [status, setStatus] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const local = localApi();
  const available = Boolean(local?.repair);
  async function run() {
    if (!local?.repair || running) return;
    const videos = (files ?? []).filter((f) => f.kind === 'video');
    setRunning(true);
    let fixed = 0;
    let failed = 0;
    try {
      for (const [i, f] of videos.entries()) {
        setStatus(`Revisando ${i + 1} de ${videos.length}: ${f.name}…`);
        try {
          const r = await local.repair(f.name);
          if (r.changed) fixed++;
          setFiles(r.files);
        } catch {
          failed++;
        }
      }
      setStatus(
        videos.length === 0
          ? 'No hay videos en esta computadora.'
          : `Listo: ${fixed === 0 ? 'todos los videos ya se podían proyectar' : `${fixed} ${fixed === 1 ? 'video convertido' : 'videos convertidos'}`}${failed ? `; ${failed} no se pudieron convertir (pueden estar dañados)` : ''}.`,
      );
    } finally {
      setRunning(false);
    }
  }
  return { available, running, status, run };
}
